import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../../redux/TranslationHelper';
import { yooptaToBackend } from '../../../utils/YooptaDataConverter';
import { ComponentRenderMode } from '../../../types/component-skin';

import { FormData, CommentData } from './Board.types';
import { adaptBoardProps } from './Board.adapter';

import { 
  fetchBoards, 
  fetchPosts, 
  fetchPostDetail, 
  createPost, 
  updatePost, 
  deletePost, 
  createComment,
  updateComment,
  deleteComment,
  selectBoards,
  selectPosts, 
  selectPostsLoading, 
  selectPostDetail, 
  selectPostDetailLoading,
  selectPostsPagination,
  selectUser,
  selectPostsData
} from '../../../redux/WithcookieSlice';

/**
 * Board 컴포넌트의 비즈니스 로직 훅
 */
export function useBoardLogic(componentData: any, mode: ComponentRenderMode) {
  const props = componentData || {};
  const { fetchBoardsOnce, fetchPostsOnce, isPreview = false } = props;
  

  // 어댑터를 통해 props 변환
  const adaptedProps = adaptBoardProps(props);
  const { style = {}, data = {} } = adaptedProps;
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { target } = useParams();
  

  // pathVariables에서 target 가져오기 (useParams가 작동하지 않는 경우 대비)
  const targetFromPathVariables = componentData?.pathVariables?.target;
  const effectiveTarget = target || targetFromPathVariables;

  // 다국어 처리를 위한 훅 사용
  const { t } = useTranslation();
  
  // 게시판, 게시글, 모드 관련 상태 관리
  const [boardMode, setBoardMode] = useState<'list' | 'detail' | 'new' | 'edit'>('list');
  const [boardCode, setBoardCode] = useState<string>('notice'); // 기본값을 'notice'로 설정
  const [postId, setPostId] = useState<string | number | null>(null);
  const [selectedBoardId, setSelectedBoardId] = useState<string | number | null>(null);
  const [formData, setFormData] = useState<FormData>({ title: '', content: '', originalBlock: null });
  const [commentData, setCommentData] = useState<CommentData>({ content: '' });
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage] = useState<number>(10);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  
  // 댓글 페이징 관련 상태
  const [currentCommentPage, setCurrentCommentPage] = useState<number>(1);
  const [commentPageSize] = useState<number>(5); // API와 동일하게 5개로 설정
  
  // 리덕스 상태 가져오기
  const boards = useSelector(selectBoards);
  const posts = useSelector(selectPosts);
  const postsLoading = useSelector(selectPostsLoading);
  const postDetail = useSelector(selectPostDetail);
  const postDetailLoading = useSelector(selectPostDetailLoading);
  const pagination = useSelector(selectPostsPagination);
  const postsData = useSelector(selectPostsData);
  
  // 현재 로그인한 사용자 정보 가져오기
  const currentUser = useSelector(selectUser);
  
  // 현재 디바이스 감지 (모바일 또는 PC)
  const [currentDevice, setCurrentDevice] = useState<'mobile' | 'pc'>(() => {
    return window.innerWidth <= 768 ? 'mobile' : 'pc';
  });
  
  // 초기 렌더링 플래그 관리
  const initialRenderRef = useRef<boolean>(true);
  const styleProcessedRef = useRef<boolean>(false);
  const loadedRef = useRef<boolean>(false);
  const urlParsingRef = useRef<boolean>(false);
  const debouncedFetchPostsRef = useRef<NodeJS.Timeout | null>(null);
  
  // 디바이스별 속성 가져오기
  const getDeviceProperty = useCallback((propertyName: string, defaultValue: any = null): any => {
    const device = currentDevice;
    
    if (data?.device?.[device]?.[propertyName] !== undefined) {
      return data.device?.[device]?.[propertyName];
    }
    
    if (data?.[propertyName] !== undefined) {
      return data[propertyName];
    }
    
    return defaultValue;
  }, [data, currentDevice]);
  
  // 게시판 제목 가져오기
  const boardTitle = useMemo(() => {
    return getDeviceProperty('title', '게시판');
  }, [getDeviceProperty]);
  
  // 버튼 표시 여부 설정
  const showNewButton = useMemo(() => getDeviceProperty('showNewButton', true), [getDeviceProperty]);
  const showEditButton = useMemo(() => getDeviceProperty('showEditButton', true), [getDeviceProperty]);
  const showDeleteButton = useMemo(() => getDeviceProperty('showDeleteButton', true), [getDeviceProperty]);
  const showCommentButton = useMemo(() => getDeviceProperty('showCommentButton', true), [getDeviceProperty]);
  
  // 게시판 파라미터 가져오기
  const boardParameter = useMemo(() => {
    return getDeviceProperty('parameter', null);
  }, [getDeviceProperty]);
  
  // 게시판 파라미터 숫자로 변환
  let categoryId: number | null = null;
  if (boardParameter) {
    if (!isNaN(Number(boardParameter))) {
      categoryId = parseInt(boardParameter as string, 10);
    } else if (typeof boardParameter === 'string') {
      const matches = boardParameter.match(/\d+/);
      if (matches && matches.length > 0) {
        categoryId = parseInt(matches[0], 10);
      }
    }
  }
  
  // 디바이스별 스타일 처리
  const computedStyle = useMemo(() => {
    let finalStyle = { ...style };
    
    if (style?.device && style.device[currentDevice]) {
      finalStyle = {
        ...finalStyle,
        ...style.device[currentDevice]
      };
    }
    
    if (style?.deviceProperty && style.deviceProperty[currentDevice]) {
      finalStyle = {
        ...finalStyle,
        ...style.deviceProperty[currentDevice]
      };
    }
    
    return finalStyle;
  }, [style, currentDevice]);
  
  // URL 파싱 로직
  useLayoutEffect(() => {
    const path = location.pathname;
    const pathSegments = path.split('/');
    

    // target이 있는 경우 처리
    if (effectiveTarget) {
      // 숫자만으로 이루어진 경우 (예: "30")
      if (/^\d+$/.test(effectiveTarget)) {
        // 게시글 상세 모드
        setBoardMode('detail');
        setPostId(parseInt(effectiveTarget, 10));
      } else if (effectiveTarget === 'new') {
        // 새 글 작성 모드
        setBoardMode('new');
      } else if (effectiveTarget.startsWith('edit')) {
        // 게시글 수정 모드 (예: "edit30")
        const id = effectiveTarget.replace('edit', '');
        if (/^\d+$/.test(id)) {
          setBoardMode('edit');
          setPostId(parseInt(id, 10));
        }
      }
    }
    
    // boardCode 설정
    // notice를 boardCode로 설정
    if (path.includes('/notice')) {
      setBoardCode('notice');
    } else {
      // effectiveTarget이 있으면 그 이전 세그먼트를 boardCode로 사용
      if (effectiveTarget) {
        // target 이전의 세그먼트 찾기
        const targetIndex = pathSegments.indexOf(effectiveTarget);
        if (targetIndex > 0) {
          setBoardCode(pathSegments[targetIndex - 1]);
        }
      } else {
        // target이 없는 경우 마지막 세그먼트를 boardCode로 사용
        const lastSegment = pathSegments.filter(seg => seg && !seg.includes(':')).pop();
        if (lastSegment) {
          setBoardCode(lastSegment);
        }
      }
    }
  }, [effectiveTarget, location.pathname]);
  
  // 게시판 목록 로드
  useEffect(() => {
    if (loadedRef.current) return;
    
    if (fetchBoardsOnce) {
      fetchBoardsOnce();
    } else {
      (dispatch as any)(fetchBoards());
    }
    
    loadedRef.current = true;
  }, [dispatch, fetchBoardsOnce]);
  
  // 게시글 목록 로드
  useEffect(() => {
    // 목록 모드일 때만 게시글 목록을 로드
    if (categoryId && boardMode === 'list') {
      if (fetchPostsOnce) {
        fetchPostsOnce(categoryId, 1, perPage);
      } else {
        (dispatch as any)(fetchPosts({
          categoryId: categoryId,
          page: 1,
          perPage
        }));
      }
      setCurrentPage(1);
      setSelectedBoardId(categoryId);
    }
  }, [categoryId, boardMode, dispatch, perPage, fetchPostsOnce]);
  
  // 상세 페이지 데이터 로드
  useEffect(() => {
    if (boardMode === 'detail' && postId) {
      // boardMode가 detail로 변경될 때 댓글 페이지를 1로 초기화
      if (currentCommentPage !== 1) {
        setCurrentCommentPage(1);
      }
      (dispatch as any)(fetchPostDetail({
        postId: postId,
        commentPage: 1, // 첫 로드 시에는 항상 1페이지
        commentTake: commentPageSize
      }));
    }
  }, [boardMode, postId, dispatch, commentPageSize]);
  
  // 수정 모드 데이터 로드
  useEffect(() => {
    if (boardMode === 'edit' && postId) {
      (dispatch as any)(fetchPostDetail({
        postId: postId,
        commentPage: 1, // 수정 모드에서는 첫 페이지부터
        commentTake: commentPageSize
      })).then((action: any) => {
        if (action.payload) {
          const post = action.payload.post || action.payload;
          setFormData({
            title: post.title || '',
            content: post.content || '',
            originalBlock: post.originalBlock || null
          });
        }
      });
    }
  }, [boardMode, postId, dispatch]);
  
  // 브라우저 뒤로가기/앞으로가기 감지
  useEffect(() => {
    const handlePopState = () => {
      window.location.reload();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location.pathname]);
  
  // 디바이스 변경 감지
  useEffect(() => {
    const handleResize = () => {
      const newDevice = window.innerWidth <= 768 ? 'mobile' : 'pc';
      if (newDevice !== currentDevice) {
        setCurrentDevice(newDevice);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentDevice]);
  
  // 댓글 페이징 처리 - 서버에서 이미 페이지네이션된 데이터를 반환하므로 그대로 사용
  const paginatedComments = useMemo(() => {

    // 서버에서 이미 페이지네이션된 댓글을 반환하므로 그대로 사용
    const comments = postDetail?.children || postDetail?.post?.children || [];

    // 서버가 이미 페이지네이션을 처리했으므로 comments를 그대로 반환
    return comments;
  }, [postDetail, currentCommentPage]);
  
  const totalCommentPages = useMemo(() => {
    // API에서 totalComments를 제공하는 경우 사용
    const totalComments = postDetail?.totalComments || postDetail?.post?.totalComments;
    if (totalComments && totalComments > 0) {
      return Math.ceil(totalComments / commentPageSize);
    }
    
    // totalComments가 없으면 children 배열 길이로 계산 (fallback)
    const comments = postDetail?.children || postDetail?.post?.children || [];
    if (!Array.isArray(comments)) {
      return 0;
    }
    return Math.ceil(comments.length / commentPageSize);
  }, [postDetail, commentPageSize]);
  
  // 페이지 버튼 범위 계산 (최대 5개, 현재 페이지 중앙)
  const commentPageRange = useMemo(() => {
    const maxButtons = 5;
    const halfRange = Math.floor(maxButtons / 2);
    
    let start = Math.max(1, currentCommentPage - halfRange);
    let end = Math.min(totalCommentPages, currentCommentPage + halfRange);
    
    // 시작이나 끝에 가까운 경우 조정
    if (end - start + 1 < maxButtons) {
      if (start === 1) {
        end = Math.min(totalCommentPages, start + maxButtons - 1);
      } else if (end === totalCommentPages) {
        start = Math.max(1, end - maxButtons + 1);
      }
    }
    
    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  }, [currentCommentPage, totalCommentPages]);
  
  // 핸들러 함수들
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);
  
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  
  const handleEditorChange = useCallback((content: string, blocks: any) => {
    const newContent = content || '';
    const newOriginalBlock = blocks ? yooptaToBackend(blocks) : null;
    
    if (formDataRef.current.content !== newContent) {
      setFormData(prev => ({ 
        ...prev, 
        content: newContent,
        originalBlock: newOriginalBlock
      }));
    }
  }, []);
  
  const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommentData(prev => ({ ...prev, content: e.target.value }));
  }, []);
  
  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
    
    if (debouncedFetchPostsRef.current) {
      clearTimeout(debouncedFetchPostsRef.current);
    }
    
    debouncedFetchPostsRef.current = setTimeout(() => {
      const boardId = categoryId || selectedBoardId;
      if (boardId) {
        const numericBoardId = Number(boardId);
        if (fetchPostsOnce) {
          fetchPostsOnce(numericBoardId, newPage, perPage);
        } else {
          (dispatch as any)(fetchPosts({
            categoryId: numericBoardId,
            page: newPage,
            perPage
          }));
        }
      }
    }, 300);
  }, [categoryId, selectedBoardId, perPage, dispatch, fetchPostsOnce]);
  
  const handleCommentPageChange = useCallback((newPage: number) => {
    setCurrentCommentPage(newPage);
    // 서버 API에 댓글 페이지 정보 전달
    if (postId) {
      (dispatch as any)(fetchPostDetail({ 
        postId: postId, 
        commentPage: newPage,
        commentTake: commentPageSize 
      }));
    }
  }, [postId, dispatch, commentPageSize]);
  
  const navigateToList = useCallback(() => {
    // boardCode가 없으면 현재 경로에서 추출하거나 기본값 사용
    const effectiveBoardCode = boardCode || 'notice';

    if (isPreview) {
      // 프리뷰 모드에서는 현재 도메인을 유지하면서 경로만 변경
      const currentOrigin = window.location.origin;
      const currentPath = window.location.pathname;
      // 현재 경로에서 웹빌더 프리뷰 경로 부분을 추출
      const pathParts = currentPath.split('/').filter(Boolean); // 빈 문자열 제거
      // finapp/gpc 등의 프리뷰 경로가 있는지 확인
      let basePath = '';
      if (currentPath.includes('/finapp/') || currentPath.includes('/gpc/')) {
        const finappIndex = pathParts.indexOf('finapp') > -1 ? pathParts.indexOf('finapp') : pathParts.indexOf('gpc');
        if (finappIndex > -1 && finappIndex + 1 < pathParts.length) {
          basePath = '/' + pathParts.slice(0, finappIndex + 2).join('/'); // finapp/번호까지 포함
        }
      }
      
      // URL 구성 시 슬래시 중복 방지 - 웹빌더에서는 :target 패턴으로
      let fullPath = basePath ? `${basePath}/${effectiveBoardCode}/:target` : `/${effectiveBoardCode}/:target`;
      // 슬래시 중복 제거
      fullPath = fullPath.replace(/\/+/g, '/');
      
      const newUrl = `${currentOrigin}${fullPath}`;
      
      window.location.href = newUrl;
    } else {
      navigate(`/${effectiveBoardCode}`);
    }
  }, [navigate, boardCode, isPreview]);
  
  const navigateToNew = useCallback(() => {
    if (isPreview) {
      // 프리뷰 모드에서는 현재 도메인을 유지하면서 경로만 변경
      const currentOrigin = window.location.origin;
      const currentPath = window.location.pathname;
      // 현재 경로에서 웹빌더 프리뷰 경로 부분을 추출
      const pathParts = currentPath.split('/');
      // finapp/gpc 등의 프리뷰 경로가 있는지 확인
      let basePath = '';
      if (currentPath.includes('/finapp/') || currentPath.includes('/gpc/')) {
        const finappIndex = pathParts.indexOf('finapp') > -1 ? pathParts.indexOf('finapp') : pathParts.indexOf('gpc');
        if (finappIndex > -1) {
          basePath = pathParts.slice(0, finappIndex + 2).join('/'); // finapp/번호까지 포함
        }
      }
      const newUrl = `${currentOrigin}${basePath}/${boardCode}/new`;
      window.location.href = newUrl;
    } else {
      navigate(`/${boardCode}/new`);
    }
  }, [navigate, boardCode, isPreview]);
  
  const navigateToDetail = useCallback((id: number | string) => {
    // boardCode가 없으면 현재 경로에서 추출하거나 기본값 사용
    const effectiveBoardCode = boardCode || 'notice';

    if (isPreview) {
      // 프리뷰 모드에서는 현재 도메인을 유지하면서 경로만 변경
      const currentOrigin = window.location.origin;
      const currentPath = window.location.pathname;
      // 현재 경로에서 웹빌더 프리뷰 경로 부분을 추출
      const pathParts = currentPath.split('/').filter(Boolean); // 빈 문자열 제거
      // finapp/gpc 등의 프리뷰 경로가 있는지 확인
      let basePath = '';
      if (currentPath.includes('/finapp/') || currentPath.includes('/gpc/')) {
        const finappIndex = pathParts.indexOf('finapp') > -1 ? pathParts.indexOf('finapp') : pathParts.indexOf('gpc');
        if (finappIndex > -1 && finappIndex + 1 < pathParts.length) {
          basePath = '/' + pathParts.slice(0, finappIndex + 2).join('/'); // finapp/번호까지 포함
        }
      }
      
      // URL 구성 시 슬래시 중복 방지
      let fullPath = basePath ? `${basePath}/${effectiveBoardCode}/${id}` : `/${effectiveBoardCode}/${id}`;
      // 슬래시 중복 제거
      fullPath = fullPath.replace(/\/+/g, '/');
      
      const newUrl = `${currentOrigin}${fullPath}`;
      
      window.location.href = newUrl;
    } else {
      navigate(`/${effectiveBoardCode}/${id}`);
    }
  }, [navigate, boardCode, isPreview]);
  
  const navigateToEdit = useCallback((id: number | string) => {
    if (isPreview) {
      // 프리뷰 모드에서는 현재 도메인을 유지하면서 경로만 변경
      const currentOrigin = window.location.origin;
      const currentPath = window.location.pathname;
      // 현재 경로에서 웹빌더 프리뷰 경로 부분을 추출
      const pathParts = currentPath.split('/');
      // finapp/gpc 등의 프리뷰 경로가 있는지 확인
      let basePath = '';
      if (currentPath.includes('/finapp/') || currentPath.includes('/gpc/')) {
        const finappIndex = pathParts.indexOf('finapp') > -1 ? pathParts.indexOf('finapp') : pathParts.indexOf('gpc');
        if (finappIndex > -1) {
          basePath = pathParts.slice(0, finappIndex + 2).join('/'); // finapp/번호까지 포함
        }
      }
      const newUrl = `${currentOrigin}${basePath}/${boardCode}/edit${id}`;
      window.location.href = newUrl;
    } else {
      navigate(`/${boardCode}/edit${id}`);
    }
  }, [navigate, boardCode, isPreview]);
  
  const handleCancelNew = useCallback(() => {
    if (isPreview) {
      setBoardMode('list');
      setFormData({ title: '', content: '', originalBlock: null });
    } else {
      navigateToList();
    }
  }, [navigateToList, isPreview]);
  
  const handleCancelEdit = useCallback(() => {
    if (isPreview) {
      setBoardMode('detail');
      setFormData({ title: '', content: '', originalBlock: null });
    } else if (postId) {
      navigateToDetail(postId);
    } else {
      navigateToList();
    }
  }, [navigateToDetail, navigateToList, postId, isPreview]);
  
  const handleSavePost = useCallback(() => {
    if (!formData.title.trim()) {
      alert(t('제목을 입력해주세요.'));
      return;
    }

    if (!formData.content.trim()) {
      alert(t('내용을 입력해주세요.'));
      return;
    }
    
    if (boardMode === 'new') {
      const boardId = categoryId || selectedBoardId;
      
      if (!boardId) {
        if (boardCode && boards && boards.length > 0) {
          const foundBoard = boards.find((board: any) => board.code === boardCode);
          if (foundBoard) {
            const numericBoardId = Number(foundBoard.id);
            
            const postData = {
              category_id: numericBoardId,
              title: formData.title,
              content: formData.content,
              edit_type: 'WYSIWYG',
              ...(formData.originalBlock && { originalBlock: formData.originalBlock })
            };
            
            (dispatch as any)(createPost(postData))
              .unwrap()
              .then((response: any) => {
                const newPostId = response.post ? response.post.id : response.id;
                
                if (newPostId) {
                  alert(t('게시글이 성공적으로 등록되었습니다.'));
                  if (isPreview) {
                    setBoardMode('detail');
                    setPostId(newPostId);
                    (dispatch as any)(fetchPostDetail(newPostId));
                  } else {
                    navigateToDetail(newPostId);
                  }
                } else {
                  alert(t('게시글이 등록되었지만 상세 보기로 이동할 수 없습니다. 목록으로 이동합니다.'));
                  if (isPreview) {
                    setBoardMode('list');
                    if (categoryId || selectedBoardId) {
                      const boardId = categoryId || selectedBoardId;
                      const numericBoardId = boardId ? Number(boardId) : null;
                      
                      if (numericBoardId) {
                        if (fetchPostsOnce) {
                          fetchPostsOnce(numericBoardId, currentPage, perPage);
                        } else {
                          (dispatch as any)(fetchPosts({
                            categoryId: numericBoardId,
                            page: currentPage,
                            perPage
                          }));
                        }
                      }
                    }
                  } else {
                    navigateToList();
                  }
                }
              })
              .catch((error: any) => {
                console.error('[게시글 생성] 실패:', error);
                alert(t('게시글 등록에 실패했습니다: ') + (error.message || t('알 수 없는 오류')));
              });
            return;
          }
        }
        
        alert(t('게시판을 선택해주세요.'));
        return;
      }
      
      const numericBoardId = Number(boardId);
      
      const postData = {
        category_id: numericBoardId,
        title: formData.title,
        content: formData.content,
        edit_type: 'WYSIWYG',
        ...(formData.originalBlock && { originalBlock: formData.originalBlock })
      };
      
      (dispatch as any)(createPost(postData))
        .unwrap()
        .then((response: any) => {
          const newPostId = response.post ? response.post.id : response.id;
          
          if (newPostId) {
            alert(t('게시글이 성공적으로 등록되었습니다.'));
            if (isPreview) {
              setBoardMode('detail');
              setPostId(newPostId);
              setCurrentCommentPage(1); // 새 게시글은 첫 페이지부터
              (dispatch as any)(fetchPostDetail({
                postId: newPostId,
                commentPage: 1,
                commentTake: commentPageSize
              }));
            } else {
              navigateToDetail(newPostId);
            }
          } else {
            alert(t('게시글이 등록되었지만 상세 보기로 이동할 수 없습니다. 목록으로 이동합니다.'));
            if (isPreview) {
              setBoardMode('list');
            } else {
              navigateToList();
            }
          }
        })
        .catch((error: any) => {
          console.error('[게시글 생성] 실패:', error);
          alert(t('게시글 등록에 실패했습니다: ') + (error.message || t('알 수 없는 오류')));
        });
    } else if (boardMode === 'edit' && postId) {
      // postId를 숫자로 변환
      const numericPostId = typeof postId === 'string' ? parseInt(postId, 10) : postId;
      
      if (isNaN(numericPostId)) {
        console.error('[게시글 수정] 유효하지 않은 postId:', postId);
        alert(t('게시글 ID가 유효하지 않습니다.'));
        return;
      }
      
      const updateData = {
        id: numericPostId,
        title: formData.title,
        content: formData.content,
        edit_type: 'WYSIWYG',
        ...(formData.originalBlock && { originalBlock: formData.originalBlock })
      };
      

      (dispatch as any)(updatePost(updateData))
        .unwrap()
        .then((response: any) => {
          alert(t('게시글이 성공적으로 수정되었습니다.'));
          if (isPreview) {
            setBoardMode('detail');
            setCurrentCommentPage(1); // 수정 후에는 첫 페이지로
            (dispatch as any)(fetchPostDetail({
              postId: numericPostId,
              commentPage: 1,
              commentTake: commentPageSize
            }));
          } else {
            navigateToDetail(numericPostId);
          }
        })
        .catch((error: any) => {
          console.error('[게시글 수정] 실패:', error);
          // 에러가 객체인 경우 메시지 추출
          const errorMessage = error?.message || error?.error || JSON.stringify(error);
          alert(t('게시글 수정에 실패했습니다: ') + errorMessage);
        });
    }
  }, [boardMode, formData, categoryId, selectedBoardId, boardCode, boards, postId, dispatch, t, navigateToDetail, navigateToList, isPreview, currentPage, perPage, fetchPostsOnce]);
  
  const handleDeletePost = useCallback(() => {
    setShowDeleteModal(true);
  }, []);
  
  const confirmDeletePost = useCallback(() => {
    if (postId) {
      (dispatch as any)(deletePost(Number(postId)))
        .unwrap()
        .then(() => {
          alert(t('게시글이 성공적으로 삭제되었습니다.'));
          setShowDeleteModal(false);
          if (isPreview) {
            setBoardMode('list');
            const boardId = categoryId || selectedBoardId;
            if (boardId) {
              const numericBoardId = Number(boardId);
              if (fetchPostsOnce) {
                fetchPostsOnce(numericBoardId, currentPage, perPage);
              } else {
                (dispatch as any)(fetchPosts({
                  categoryId: numericBoardId,
                  page: currentPage,
                  perPage
                }));
              }
            }
          } else {
            navigateToList();
          }
        })
        .catch((error: any) => {
          console.error('[게시글 삭제] 실패:', error);
          alert(t('게시글 삭제에 실패했습니다: ') + (error.message || t('알 수 없는 오류')));
          setShowDeleteModal(false);
        });
    }
  }, [postId, dispatch, t, navigateToList, isPreview, categoryId, selectedBoardId, currentPage, perPage, fetchPostsOnce]);
  
  const cancelDeletePost = useCallback(() => {
    setShowDeleteModal(false);
  }, []);
  
  const handleCommentSubmit = useCallback(() => {
    if (!commentData.content.trim()) {
      alert(t('댓글 내용을 입력해주세요.'));
      return;
    }
    
    if (!postId) return;
    
    const commentPayload = {
      postId: Number(postId),
      commentData: {
        content: commentData.content,
        edit_type: 'WYSIWYG'
      }
    };
    
    (dispatch as any)(createComment(commentPayload))
      .unwrap()
      .then(() => {
        alert(t('댓글이 성공적으로 등록되었습니다.'));
        setCommentData({ content: '' });
        (dispatch as any)(fetchPostDetail({
          postId: postId,
          commentPage: currentCommentPage,
          commentTake: commentPageSize
        }));
      })
      .catch((error: any) => {
        console.error('[댓글 생성] 실패:', error);
        alert(t('댓글 등록에 실패했습니다: ') + (error.message || t('알 수 없는 오류')));
      });
  }, [commentData.content, postId, dispatch, t]);
  
  const handleEditComment = useCallback((commentId: number, content: string) => {
    setEditingCommentId(commentId);
    setEditingCommentContent(content);
  }, []);
  
  const handleUpdateComment = useCallback(() => {
    if (!editingCommentContent.trim()) {
      alert(t('댓글 내용을 입력해주세요.'));
      return;
    }
    
    if (!editingCommentId || !postId) return;
    
    const updateData = {
      postId: Number(postId),
      commentId: editingCommentId,
      commentData: {
        content: editingCommentContent,
        edit_type: 'WYSIWYG'
      }
    };
    
    (dispatch as any)(updateComment(updateData))
      .unwrap()
      .then(() => {
        alert(t('댓글이 성공적으로 수정되었습니다.'));
        setEditingCommentId(null);
        setEditingCommentContent('');
        (dispatch as any)(fetchPostDetail({
          postId: postId,
          commentPage: currentCommentPage,
          commentTake: commentPageSize
        }));
      })
      .catch((error: any) => {
        console.error('[댓글 수정] 실패:', error);
        alert(t('댓글 수정에 실패했습니다: ') + (error.message || t('알 수 없는 오류')));
      });
  }, [editingCommentContent, editingCommentId, postId, dispatch, t]);
  
  const handleCancelEditComment = useCallback(() => {
    setEditingCommentId(null);
    setEditingCommentContent('');
  }, []);
  
  const handleDeleteComment = useCallback((commentId: number) => {
    if (!window.confirm(t('댓글을 삭제하시겠습니까?'))) return;
    
    if (!postId) return;
    
    (dispatch as any)(deleteComment({ postId: Number(postId), commentId: commentId }))
      .unwrap()
      .then(() => {
        alert(t('댓글이 성공적으로 삭제되었습니다.'));
        (dispatch as any)(fetchPostDetail({
          postId: postId,
          commentPage: currentCommentPage,
          commentTake: commentPageSize
        }));
      })
      .catch((error: any) => {
        console.error('[댓글 삭제] 실패:', error);
        alert(t('댓글 삭제에 실패했습니다: ') + (error.message || t('알 수 없는 오류')));
      });
  }, [postId, dispatch, t]);
  
  const handleEditingCommentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditingCommentContent(e.target.value);
  }, []);
  
  // Editor 초기 데이터 설정 (Yoopta)
  const editorInitialData = useMemo(() => {

    if (boardMode === 'edit') {
      try {
        // 수정 모드에서는 formData의 originalBlock을 우선 사용
        if (formData.originalBlock) {
          // null이나 undefined 체크
          if (typeof formData.originalBlock !== 'object' || formData.originalBlock === null) {
            console.error('[BoardLogic] originalBlock is not valid:', formData.originalBlock);
            return null;
          }
          
          // Yoopta 에디터가 필요로 하는 구조로 변환
          let editorData = { ...formData.originalBlock };
          
          // blocks를 elements로 변환 (API 응답 구조 대응)
          if (editorData.blocks && !editorData.elements) {
            editorData.elements = editorData.blocks;
            delete editorData.blocks;
          }
          
          // elements가 없거나 비어있으면 null 반환
          if (!editorData.elements || Object.keys(editorData).length === 0) {
            return null;
          }
          
          // 각 블록의 value 배열이 있는지 확인하고 meta 정보 추가
          let blockIndex = 0;
          Object.keys(editorData).forEach(blockId => {
            const block = editorData[blockId];
            if (block && typeof block === 'object') {
              // id가 없으면 blockId를 사용
              if (!block.id) {
                block.id = blockId;
              }
              // type이 없으면 기본값 설정
              if (!block.type) {
                block.type = 'Paragraph';
              }
              // value가 없으면 빈 배열로 초기화
              if (!block.value) {
                block.value = [];
              }
              // value가 배열이 아니면 배열로 변환
              if (!Array.isArray(block.value)) {
                block.value = [block.value];
              }
              // value의 각 요소가 올바른 구조인지 확인
              block.value = block.value.map((item: any, index: number) => {
                if (!item || typeof item !== 'object') {
                  return {
                    id: `element-${blockId}-${index}`,
                    type: 'paragraph',
                    children: [{ text: String(item || '') }]
                  };
                }
                // children이 없으면 추가
                if (!item.children) {
                  item.children = [{ text: '' }];
                }
                return item;
              });
              // meta 정보가 없으면 추가
              if (!block.meta) {
                block.meta = {};
              }
              // order가 없으면 추가
              if (typeof block.meta.order !== 'number') {
                block.meta.order = blockIndex++;
              }
              // depth가 없으면 0으로 설정
              if (typeof block.meta.depth !== 'number') {
                block.meta.depth = 0;
              }
            }
          });
          
          return editorData;
        }
        
        // postDetail에서도 확인
        const originalBlock = postDetail?.originalBlock || postDetail?.post?.originalBlock;
        if (originalBlock) {
          if (typeof originalBlock !== 'object' || originalBlock === null) {
            console.error('[BoardLogic] originalBlock from postDetail is not valid:', originalBlock);
            return null;
          }
          
          let editorData = { ...originalBlock };
          
          // blocks를 elements로 변환
          if (editorData.blocks && !editorData.elements) {
            editorData.elements = editorData.blocks;
            delete editorData.blocks;
          }
          
          // elements가 없거나 비어있으면 null 반환
          if (!editorData.elements || Object.keys(editorData).length === 0) {
            return null;
          }
          
          // 각 블록의 value 배열 확인하고 meta 정보 추가
          let blockIndex = 0;
          Object.keys(editorData).forEach(blockId => {
            const block = editorData[blockId];
            if (block && typeof block === 'object') {
              // id가 없으면 blockId를 사용
              if (!block.id) {
                block.id = blockId;
              }
              // type이 없으면 기본값 설정
              if (!block.type) {
                block.type = 'Paragraph';
              }
              if (!block.value) {
                block.value = [];
              }
              if (!Array.isArray(block.value)) {
                block.value = [block.value];
              }
              // value의 각 요소가 올바른 구조인지 확인
              block.value = block.value.map((item: any, index: number) => {
                if (!item || typeof item !== 'object') {
                  return {
                    id: `element-${blockId}-${index}`,
                    type: 'paragraph',
                    children: [{ text: String(item || '') }]
                  };
                }
                // children이 없으면 추가
                if (!item.children) {
                  item.children = [{ text: '' }];
                }
                return item;
              });
              // meta 정보가 없으면 추가
              if (!block.meta) {
                block.meta = {};
              }
              // order가 없으면 추가
              if (typeof block.meta.order !== 'number') {
                block.meta.order = blockIndex++;
              }
              // depth가 없으면 0으로 설정
              if (typeof block.meta.depth !== 'number') {
                block.meta.depth = 0;
              }
            }
          });
          
          return editorData;
        }
      } catch (error) {
        console.error('[BoardLogic] Error processing editorInitialData:', error);
        return null;
      }
    }
    
    // edit 모드가 아니거나 데이터가 없으면 null 반환
    return null;
  }, [boardMode, postDetail, formData.originalBlock]);
  
  // finapp 모드 확인
  const isFinappMode = location.pathname.includes('/finapp/') || location.pathname.includes('/gpc/');
  
  // 스킨 시스템에 맞는 data/actions 구조로 반환
  return {
    data: {
      // Props
      style: computedStyle,
      data: data,
      
      // States
      mode: boardMode,
      boardCode,
      postId,
      selectedBoardId,
      formData,
      commentData,
      editingCommentId,
      editingCommentContent,
      currentPage,
      perPage,
      showDeleteModal,
      currentCommentPage,
      commentPageSize,
      
      // Redux states
      boards,
      posts,
      postsLoading,
      postDetail,
      postDetailLoading,
      pagination,
      postsData,
      currentUser,
      
      // Computed values
      currentDevice,
      boardTitle,
      showNewButton,
      showEditButton,
      showDeleteButton,
      showCommentButton,
      categoryId,
      paginatedComments,
      totalCommentPages,
      commentPageRange,
      editorInitialData,
      isFinappMode,
      isPreview,
      
      // Refs
      initialRenderRef,
      styleProcessedRef,
      loadedRef,
      urlParsingRef,
      debouncedFetchPostsRef,
      
      // Utils
      t,
      getDeviceProperty
    },
    actions: {
      // State setters
      setBoardMode,
      setBoardCode,
      setPostId,
      setSelectedBoardId,
      setFormData,
      setCommentData,
      setEditingCommentId,
      setEditingCommentContent,
      setCurrentPage,
      setShowDeleteModal,
      setCurrentCommentPage,
      setCurrentDevice,
      
      // Handlers
      handleInputChange,
      handleEditorChange,
      handleCommentChange,
      handlePageChange,
      handleCommentPageChange,
      handleSavePost,
      handleDeletePost,
      confirmDeletePost,
      cancelDeletePost,
      handleCommentSubmit,
      handleEditComment,
      handleUpdateComment,
      handleCancelEditComment,
      handleDeleteComment,
      handleEditingCommentChange,
      handleCancelNew,
      handleCancelEdit,
      
      // Navigation
      navigateToList,
      navigateToNew,
      navigateToDetail,
      navigateToEdit
    }
  };
}

export default useBoardLogic;